/**
 * Utility functions for extracting image dimensions from File/Blob
 * Works in Cloudflare Workers environment
 */

export interface ImageDimensions {
  width: number;
  height: number;
}

// Blob and File instances are immutable, so their dimensions can be safely
// cached by identity. Keeping the in-flight Promise is important for the UI:
// pricing polls more frequently than a large file may finish reading, and each
// poll must join the existing read instead of starting another one.
const imageDimensionsCache = new WeakMap<Blob, Promise<ImageDimensions | null>>();

/**
 * Extract image dimensions from a File or Blob
 * Supports PNG, JPEG, GIF, WEBP formats
 */
export function getImageDimensions(file: File | Blob): Promise<ImageDimensions | null> {
  const cached = imageDimensionsCache.get(file);
  if (cached !== undefined) {
    return cached;
  }

  const extraction = extractImageDimensions(file);
  imageDimensionsCache.set(file, extraction);
  return extraction;
}

async function extractImageDimensions(file: File | Blob): Promise<ImageDimensions | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);

    // Check file type by magic bytes
    if (isPNG(bytes)) {
      return getPNGDimensions(bytes);
    } else if (isJPEG(bytes)) {
      return getJPEGDimensions(bytes);
    } else if (isGIF(bytes)) {
      return getGIFDimensions(bytes);
    } else if (isWEBP(bytes)) {
      return getWEBPDimensions(bytes);
    }

    console.warn('⚠️ Unsupported image format for dimension extraction');
    return null;
  } catch (error: any) {
    console.error('❌ Failed to extract image dimensions:', error.message);
    return null;
  }
}

/**
 * Extract dimensions from multiple images
 */
export async function getMultipleImageDimensions(files: File[]): Promise<ImageDimensions[]> {
  const dimensions = await Promise.all(
    files.map(file => getImageDimensions(file))
  );

  // Filter out null results (unsupported formats)
  return dimensions.filter((dim): dim is ImageDimensions => dim !== null);
}

// File type detection helpers
function isPNG(bytes: Uint8Array): boolean {
  return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
}

function isJPEG(bytes: Uint8Array): boolean {
  return bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
}

function isGIF(bytes: Uint8Array): boolean {
  return bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46;
}

function isWEBP(bytes: Uint8Array): boolean {
  return bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
}

// PNG dimensions extraction
function getPNGDimensions(bytes: Uint8Array): ImageDimensions {
  // PNG IHDR chunk is at offset 16-24
  // Width: bytes 16-19 (big-endian)
  // Height: bytes 20-23 (big-endian)
  const width = (bytes[16] << 24) | (bytes[17] << 16) | (bytes[18] << 8) | bytes[19];
  const height = (bytes[20] << 24) | (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];

  return { width, height };
}

// JPEG dimensions extraction
function getJPEGDimensions(bytes: Uint8Array): ImageDimensions | null {
  let offset = 2; // Skip SOI marker

  while (offset < bytes.length) {
    // Check for marker
    if (bytes[offset] !== 0xFF) break;

    const marker = bytes[offset + 1];
    offset += 2;

    // SOF markers (Start of Frame)
    if (marker >= 0xC0 && marker <= 0xCF && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) {
      // Skip length (2 bytes) and precision (1 byte)
      offset += 3;

      // Height (2 bytes, big-endian)
      const height = (bytes[offset] << 8) | bytes[offset + 1];
      offset += 2;

      // Width (2 bytes, big-endian)
      const width = (bytes[offset] << 8) | bytes[offset + 1];

      return { width, height };
    }

    // Skip segment
    const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
    offset += segmentLength;
  }

  return null;
}

// GIF dimensions extraction
function getGIFDimensions(bytes: Uint8Array): ImageDimensions {
  // GIF dimensions are at offset 6-9 (little-endian)
  const width = bytes[6] | (bytes[7] << 8);
  const height = bytes[8] | (bytes[9] << 8);

  return { width, height };
}

// WebP dimensions extraction
function getWEBPDimensions(bytes: Uint8Array): ImageDimensions | null {
  // WebP format is complex, check for VP8/VP8L/VP8X
  const chunkType = String.fromCharCode(bytes[12], bytes[13], bytes[14], bytes[15]);

  if (chunkType === 'VP8 ') {
    // VP8 bitstream dimensions at offset 26-29
    const width = ((bytes[26] | (bytes[27] << 8)) & 0x3FFF) + 1;
    const height = ((bytes[28] | (bytes[29] << 8)) & 0x3FFF) + 1;
    return { width, height };
  } else if (chunkType === 'VP8L') {
    // VP8L dimensions (14-bit values)
    const bits = (bytes[21] << 16) | (bytes[22] << 8) | bytes[23];
    const width = ((bits & 0x3FFF) + 1);
    const height = (((bits >> 14) & 0x3FFF) + 1);
    return { width, height };
  } else if (chunkType === 'VP8X') {
    // Extended format (24-bit values + 1)
    const width = (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16)) + 1;
    const height = (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16)) + 1;
    return { width, height };
  }

  return null;
}
